import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { FaTruck } from "react-icons/fa";
import { GrConfigure } from 'react-icons/gr';
import './OperationalCostsNode.css'; // Import the CSS file
import OperationalCostsConfigModal from './OperationalCostsConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';

const OperationalCostsNode = ({ id, data, selected }) => {
  const { 
    no_of_bop_year, 
    wind_bop_escalation_perc,
    wind_escalation_perc,
    solar_escalation_perc,
    discount_rate,
    annual_maintenance_per_turbine_in_lakhs,
    annual_maintenance_per_turbine_in_lakhs_with_bop,
    annual_maintenance_per_solar_unit_in_lakhs,
    insurance_percent_of_asset_cost,
    operational_charges,
    asset_management_fees,
    annual_lease_charge_per_acre_solar,
    annual_lease_charge_per_acre_wind,
    annual_lease_escalation_perc,
    solar_acres_per_MWp,
    wind_acres_per_WTG,
    first_year_benifits,
    revenue_from_excess_banked_units_per_unit,
    total_lapsed_units_for_excess_revenue_perc,
    transmission_charges_per_KWh,
    wheeling_charges,
    electricity_duty_per_unit,
    banking_charge_rs,
    per_KW,
    per_KWh,
    transmission_charges_per_Kw_per_month,
    include_depreciation,
    name 
  } = data;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addOperationalCostsNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`OperationalCostsNode ${id} received data:`, data);
    
    // Log reconfiguration state
    console.log(`OperationalCostsNode ${id} reconfiguration state:`, {
      isConfigured: data.isConfigured,
      isApiConfigured: data.isApiConfigured,
      needsReconfiguration: data.needsReconfiguration,
      needsPartialReconfiguration: data.needsPartialReconfiguration
    });
  }, [id, data]);

  // Check if node is configured properly
  const isConfigured = data.isConfigured || data.isApiConfigured || (
    no_of_bop_year !== undefined && 
    wind_bop_escalation_perc !== undefined &&
    wind_escalation_perc !== undefined &&
    solar_escalation_perc !== undefined
  );

  // Check if this node has all three input connections
  const hasExtrapolationInputConnection = edges.some(edge => edge.target === id && edge.targetHandle === 'extrapolation_input');
  const hasLoanInputConnection = edges.some(edge => edge.target === id && edge.targetHandle === 'loan_input');
  const hasDepreciationInputConnection = edges.some(edge => edge.target === id && edge.targetHandle === 'depreciation_input');
  const hasAllInputConnections = hasExtrapolationInputConnection && hasLoanInputConnection && hasDepreciationInputConnection;
  const hasAtLeastOneInput = hasExtrapolationInputConnection || hasLoanInputConnection || hasDepreciationInputConnection;
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node names if there are connections
  const getInputNodeNames = useCallback(() => {
    const extrapolationInputEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'extrapolation_input');
    const loanInputEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'loan_input');
    const depreciationInputEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'depreciation_input');
    
    const result = {
      extrapolation_input: null,
      loan_input: null,
      depreciation_input: null
    };
    
    // Process extrapolation input
    if (extrapolationInputEdge) {
      const sourceNodeId = extrapolationInputEdge.source;
      const sourceNode = nodes.find(node => node.id === sourceNodeId);
      
      if (sourceNode && sourceNode.data && sourceNode.data.name) {
        result.extrapolation_input = sourceNode.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.extrapolation_input = nodeMapping[sourceNodeId] || sourceNodeId;
      }
    }
    
    // Process loan input
    if (loanInputEdge) {
      const sourceNodeId = loanInputEdge.source;
      const sourceNode = nodes.find(node => node.id === sourceNodeId);
      
      if (sourceNode && sourceNode.data && sourceNode.data.name) {
        result.loan_input = sourceNode.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.loan_input = nodeMapping[sourceNodeId] || sourceNodeId;
      }
    }
    
    // Process depreciation input
    if (depreciationInputEdge) {
      const sourceNodeId = depreciationInputEdge.source;
      const sourceNode = nodes.find(node => node.id === sourceNodeId);
      
      if (sourceNode && sourceNode.data && sourceNode.data.name) {
        result.depreciation_input = sourceNode.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.depreciation_input = nodeMapping[sourceNodeId] || sourceNodeId;
      }
    }
    
    console.log(`OperationalCostsNode ${id} input connections:`, result);
    
    return result;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Function to check if a stored input node name matches any current connection
  const hasMatchingInput = useCallback((storedNodeName, inputType) => {
    if (!storedNodeName) return true; // If no stored name, consider it matching
    
    const inputNodes = getInputNodeNames();
    if (inputType === 'extrapolation') {
      return inputNodes.extrapolation_input === storedNodeName;
    } else if (inputType === 'loan') {
      return inputNodes.loan_input === storedNodeName;
    } else if (inputType === 'depreciation') {
      return inputNodes.depreciation_input === storedNodeName;
    }
    return false;
  }, [getInputNodeNames]);
  
  // Check if the node needs reconfiguration
  const needsReconfiguration = data.needsReconfiguration || data.isApiConfigured === false || 
    (isConfigured && data.extrapolationInputNodeName && data.loanInputNodeName && data.depreciationInputNodeName && 
      (!hasMatchingInput(data.extrapolationInputNodeName, 'extrapolation') || 
       !hasMatchingInput(data.loanInputNodeName, 'loan') || 
       !hasMatchingInput(data.depreciationInputNodeName, 'depreciation')));
      
  // Check if this node needs partial reconfiguration (one or more inputs were deleted)
  const needsPartialReconfiguration = data.needsPartialReconfiguration || 
    (isConfigured && ((data.extrapolationInputNodeName && !hasMatchingInput(data.extrapolationInputNodeName, 'extrapolation')) || 
                     (data.loanInputNodeName && !hasMatchingInput(data.loanInputNodeName, 'loan')) ||
                     (data.depreciationInputNodeName && !hasMatchingInput(data.depreciationInputNodeName, 'depreciation'))));

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);
  
  // Log computed reconfiguration state for debugging
  useEffect(() => {
    console.log(`OperationalCostsNode ${id} computed reconfiguration state:`, {
      isConfigured,
      needsReconfiguration,
      needsPartialReconfiguration,
      hasExtrapolationInputConnection,
      hasLoanInputConnection,
      hasDepreciationInputConnection,
      hasAllInputConnections,
      hasAtLeastOneInput
    });
  }, [id, isConfigured, needsReconfiguration, needsPartialReconfiguration, 
      hasExtrapolationInputConnection, hasLoanInputConnection, hasDepreciationInputConnection, 
      hasAllInputConnections, hasAtLeastOneInput]);
  
  // Function to handle reconfiguration of the input nodes
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node names
      const inputNodes = getInputNodeNames();
      
      console.log(`Reconfiguring node ${name} with input nodes:`, inputNodes);
      
      // Update the UI first to provide immediate feedback
      const updateNodeUI = (extrapolationInputName, loanInputName, depreciationInputName) => {
        // Update the node data
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false,
                  extrapolationInputNodeName: extrapolationInputName,
                  loanInputNodeName: loanInputName,
                  depreciationInputNodeName: depreciationInputName,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update the session state
        const position = getNodePosition();
        const params = {
          noOfBopYear: no_of_bop_year || 0,
          windBopEscalationPerc: wind_bop_escalation_perc || 0,
          windEscalationPerc: wind_escalation_perc || 0,
          solarEscalationPerc: solar_escalation_perc || 0,
          discountRate: discount_rate || 0,
          annualMaintenancePerTurbineInLakhs: annual_maintenance_per_turbine_in_lakhs || 0,
          annualMaintenancePerTurbineInLakhsWithBop: annual_maintenance_per_turbine_in_lakhs_with_bop || 0,
          annualMaintenancePerSolarUnitInLakhs: annual_maintenance_per_solar_unit_in_lakhs || 0,
          insurancePercentOfAssetCost: insurance_percent_of_asset_cost || 0,
          operationalCharges: operational_charges || 0,
          assetManagementFees: asset_management_fees || 0,
          annualLeaseChargePerAcreSolar: annual_lease_charge_per_acre_solar ?? 0,
          annualLeaseChargePerAcreWind: annual_lease_charge_per_acre_wind ?? 0,
          annualLeaseEscalationPerc: annual_lease_escalation_perc || 0,
          solarAcresPerMWp: solar_acres_per_MWp || 0,
          windAcresPerWTG: wind_acres_per_WTG || 0,
          firstYearBenifits: first_year_benifits || true,
          revenueFromExcessBankedUnitsPerUnit: revenue_from_excess_banked_units_per_unit || 0,
          totalLapsedUnitsForExcessRevenuePerc: total_lapsed_units_for_excess_revenue_perc || 0,
          transmissionChargesPerKWh: transmission_charges_per_KWh || 0,
          wheelingCharges: wheeling_charges || 0,
          electricityDutyPerUnit: electricity_duty_per_unit || 0,
          bankingChargeRs: banking_charge_rs ?? 0,
          perKW: per_KW || false,
          perKWh: per_KWh || false,
          transmissionChargesPerKwPerMonth: transmission_charges_per_Kw_per_month || 0
        };
        
        addOperationalCostsNode(name, params, position);
      };
      
      // For operational costs node, we need all three inputs for full reconfiguration
      if (!inputNodes.extrapolation_input || !inputNodes.loan_input || !inputNodes.depreciation_input) {
        throw new Error('All three input nodes (extrapolation, loan, depreciation) must be connected for reconfiguration');
      }
      
      let apiError = null;
      
      try {
        // Reconfigure with the extrapolation input
        await reconfigureNode(
          session.pipeline_name,
          name,
          inputNodes.extrapolation_input,
          0  // inputIndex = 0 for the extrapolation input
        );
        console.log(`Node ${name} extrapolation input reconfigured to ${inputNodes.extrapolation_input}`);
      } catch (error) {
        console.error('API error reconfiguring extrapolation input:', error);
        apiError = error;
      }
      
      try {
        // Reconfigure with the loan input
        await reconfigureNode(
          session.pipeline_name,
          name,
          inputNodes.loan_input,
          1  // inputIndex = 1 for the loan input
        );
        console.log(`Node ${name} loan input reconfigured to ${inputNodes.loan_input}`);
      } catch (error) {
        console.error('API error reconfiguring loan input:', error);
        if (!apiError) apiError = error;
      }
      
      try {
        // Reconfigure with the depreciation input
        await reconfigureNode(
          session.pipeline_name,
          name,
          inputNodes.depreciation_input,
          2  // inputIndex = 2 for the depreciation input
        );
        console.log(`Node ${name} depreciation input reconfigured to ${inputNodes.depreciation_input}`);
      } catch (error) {
        console.error('API error reconfiguring depreciation input:', error);
        if (!apiError) apiError = error;
      }
      
      // Update the UI even if API calls failed
      updateNodeUI(inputNodes.extrapolation_input, inputNodes.loan_input, inputNodes.depreciation_input);
      
      // If there was an API error, log it but don't alert the user since the UI is updated
      if (apiError) {
        console.warn('API errors occurred during reconfiguration, but UI was updated:', apiError);
      }
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error in reconfiguration process:', error);
      // Update the UI anyway to prevent the node from being stuck in reconfiguration state
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                needsReconfiguration: false,
                needsPartialReconfiguration: false,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      setIsConfiguring(false);
    }
  };

  const openConfigModal = useCallback(() => {
    // For initial configuration, we need all three inputs
    if (!isConfigured && !hasAllInputConnections) {
      notifyError(new Error("Please connect all three inputs (extrapolation, loan, depreciation) to this operational costs node before configuring it."), {
        toastId: 'opcost.connectAllInputs',
      });
      return;
    }
    
    // For reconfiguration, we need at least one input
    if ((needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) && !hasAtLeastOneInput) {
      notifyError(new Error("Please connect at least one input to this operational costs node before reconfiguring it."), {
        toastId: 'opcost.connectOneInput',
      });
      return;
    }
    
    // If the node is already configured and the user is trying to edit it (not reconfigure)
    if (isConfigured && !needsReconfiguration && !needsPartialReconfiguration && data.isApiConfigured !== false) {
      // Open the modal in edit mode
      console.log("Opening modal for editing existing operational costs node");
      setIsModalOpen(true);
      return;
    }
    
    setIsModalOpen(true);
  }, [hasAllInputConnections, hasAtLeastOneInput, isConfigured, needsReconfiguration, needsPartialReconfiguration, data.isApiConfigured]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback((configData) => {
    console.log('Operational costs node configured with data:', configData);
    
    // Get the input node names
    const inputNodes = getInputNodeNames();
    
    // Update the node data
    reactFlowInstance.setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              ...configData,
              isConfigured: true,
              needsReconfiguration: false,
              needsPartialReconfiguration: false,
              extrapolationInputNodeName: inputNodes.extrapolation_input,
              loanInputNodeName: inputNodes.loan_input,
              depreciationInputNodeName: inputNodes.depreciation_input,
              isApiConfigured: true
            }
          };
        }
        return node;
      })
    );
    
    // Update the session state
    const position = getNodePosition();
    addOperationalCostsNode(
      configData.name,
      {
        noOfBopYear: configData.no_of_bop_year,
        windBopEscalationPerc: configData.wind_bop_escalation_perc,
        windEscalationPerc: configData.wind_escalation_perc,
        solarEscalationPerc: configData.solar_escalation_perc,
        discountRate: configData.discount_rate,
        annualMaintenancePerTurbineInLakhs: configData.annual_maintenance_per_turbine_in_lakhs,
        annualMaintenancePerTurbineInLakhsWithBop: configData.annual_maintenance_per_turbine_in_lakhs_with_bop,
        annualMaintenancePerSolarUnitInLakhs: configData.annual_maintenance_per_solar_unit_in_lakhs,
        insurancePercentOfAssetCost: configData.insurance_percent_of_asset_cost,
        operationalCharges: configData.operational_charges,
        assetManagementFees: configData.asset_management_fees,
        annualLeaseChargePerAcreSolar: configData.annual_lease_charge_per_acre_solar ?? configData.annual_lease_charge_per_acre,
        annualLeaseChargePerAcreWind: configData.annual_lease_charge_per_acre_wind ?? configData.annual_lease_charge_per_acre,
        annualLeaseEscalationPerc: configData.annual_lease_escalation_perc,
        solarAcresPerMWp: configData.solar_acres_per_MWp,
        windAcresPerWTG: configData.wind_acres_per_WTG,
        firstYearBenifits: configData.first_year_benifits,
        revenueFromExcessBankedUnitsPerUnit: configData.revenue_from_excess_banked_units_per_unit,
        totalLapsedUnitsForExcessRevenuePerc: configData.total_lapsed_units_for_excess_revenue_perc,
        transmissionChargesPerKWh: configData.transmission_charges_per_KWh,
        wheelingCharges: configData.wheeling_charges,
        electricityDutyPerUnit: configData.electricity_duty_per_unit,
        bankingChargeRs: configData.banking_charge_rs ?? banking_charge_rs ?? 0
      },
      { x: position.x, y: position.y }
    );
    
    // Update the node mapping
    updateNodeMapping(id, configData.name);
    
    // Close the modal
    closeConfigModal();
  }, [id, reactFlowInstance, getInputNodeNames, getNodePosition, addOperationalCostsNode, updateNodeMapping, closeConfigModal]);

  // Handle node selection
  const handleNodeClick = useCallback(() => {
    updateSelectedNode(name);
  }, [name, updateSelectedNode]);

  return (
    <div 
      className={`operational-costs-node ${selected ? 'operational-costs-selected' : ''} ${
        needsReconfiguration ? 'operational-costs-needs-reconfiguration' : ''
      }`}
      onClick={handleNodeClick}
    >
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ right: '-6px' }}
        className={`operational-costs-connection-point ${
          isConfigured ? 'operational-costs-configured-output' : 'operational-costs-not-configured'
        }`}
      />
      
      <div className={`operational-costs-node-header ${
        isConfigured && !needsReconfiguration ? 'operational-costs-configured' : 
        needsReconfiguration ? 'operational-costs-needs-reconfiguration' : 
        'operational-costs-not-configured'
      }`}>
        <div className="operational-costs-node-header-title">
          <div className="operational-costs-node-type">Operational Costs</div>
          {name && <div className="operational-costs-node-name-display">{name}</div>}
        </div>
        <div className="operational-costs-node-header-icons">
          <FaTruck className="operational-costs-icon" />
          {needsReconfiguration && !isConfiguring && (
            <button
              className="operational-costs-config-button operational-costs-reconfigure-button"
              onClick={handleReconfigureInput}
              title="Reconfigure input connections"
            >
              <GrConfigure size={16} />
            </button>
          )}
          <button
            className="operational-costs-config-button"
            onClick={openConfigModal}
            disabled={isConfiguring}
            title={isConfigured ? "Edit operational costs node" : "Configure operational costs node"}
          >
            {isConfigured ? <Pencil1Icon /> : <GrConfigure />}
          </button>
        </div>
      </div>
      
      <div className="operational-costs-node-content">
        {!isConfigured && (
          <div className="operational-costs-text-xs operational-costs-text-warning">
            Not configured
          </div>
        )}
        {isConfigured && !needsReconfiguration && (
          <div className="operational-costs-text-xs operational-costs-text-info">
            Configured
          </div>
        )}
        {isConfigured && (
          <div className="operational-costs-text-xs">
            <div>Excess banked rate: {Number(revenue_from_excess_banked_units_per_unit || 0)} INR/unit</div>
            <div>Lapsed units eligible: {Number(total_lapsed_units_for_excess_revenue_perc || 0)}%</div>
          </div>
        )}
        {needsReconfiguration && (
          <div className="operational-costs-text-xs operational-costs-text-warning">
            <ExclamationTriangleIcon style={{ display: 'inline', marginRight: '4px' }} />
            Needs reconfiguration
          </div>
        )}
        {isConfiguring && (
          <div className="operational-costs-text-xs operational-costs-text-info">
            Reconfiguring...
          </div>
        )}
        
        <div className="operational-costs-input-list">
          <div className="operational-costs-input-row">
            <Handle
              type="target"
              position={Position.Left}
              id="extrapolation_input"
              style={{ top: '50%', left: '-14px' }}
              className={`operational-costs-connection-point operational-costs-extrapolation-input ${
                isConfigured ? 'operational-costs-configured' : 'operational-costs-not-configured'
              }`}
            />
            <span className="operational-costs-text-xs">Extrapolation</span>
          </div>
          <div className="operational-costs-input-row">
            <Handle
              type="target"
              position={Position.Left}
              id="loan_input"
              style={{ top: '50%', left: '-14px' }}
              className={`operational-costs-connection-point operational-costs-loan-input ${
                isConfigured ? 'operational-costs-configured' : 'operational-costs-not-configured'
              }`}
            />
            <span className="operational-costs-text-xs">Loan</span>
          </div>
          <div className="operational-costs-input-row">
            <Handle
              type="target"
              position={Position.Left}
              id="depreciation_input"
              style={{ top: '50%', left: '-14px' }}
              className={`operational-costs-connection-point operational-costs-depreciation-input ${
                isConfigured ? 'operational-costs-configured' : 'operational-costs-not-configured'
              }`}
            />
            <span className="operational-costs-text-xs">Depreciation</span>
          </div>
        </div>
      </div>
      
      <OperationalCostsConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={needsReconfiguration || needsPartialReconfiguration}
        initialData={{
          name: name,
          no_of_bop_year: no_of_bop_year,
          wind_bop_escalation_perc: wind_bop_escalation_perc,
          wind_escalation_perc: wind_escalation_perc,
          solar_escalation_perc: solar_escalation_perc,
          discount_rate: discount_rate,
          annual_maintenance_per_turbine_in_lakhs: annual_maintenance_per_turbine_in_lakhs,
          annual_maintenance_per_turbine_in_lakhs_with_bop: annual_maintenance_per_turbine_in_lakhs_with_bop,
          annual_maintenance_per_solar_unit_in_lakhs: annual_maintenance_per_solar_unit_in_lakhs,
          insurance_percent_of_asset_cost: insurance_percent_of_asset_cost,
          operational_charges: operational_charges,
          asset_management_fees: asset_management_fees,
          annual_lease_charge_per_acre_solar: annual_lease_charge_per_acre_solar ?? 0,
          annual_lease_charge_per_acre_wind: annual_lease_charge_per_acre_wind ?? 0,
          annual_lease_escalation_perc: annual_lease_escalation_perc,
          solar_acres_per_MWp: solar_acres_per_MWp,
          wind_acres_per_WTG: wind_acres_per_WTG,
          first_year_benifits: first_year_benifits,
          revenue_from_excess_banked_units_per_unit: revenue_from_excess_banked_units_per_unit,
          total_lapsed_units_for_excess_revenue_perc: total_lapsed_units_for_excess_revenue_perc,
          transmission_charges_per_KWh: transmission_charges_per_KWh,
          wheeling_charges: wheeling_charges,
          electricity_duty_per_unit: electricity_duty_per_unit,
          banking_charge_rs: banking_charge_rs,
          per_KW: per_KW,
          per_KWh: per_KWh,
          transmission_charges_per_Kw_per_month: transmission_charges_per_Kw_per_month,
          include_depreciation: include_depreciation,
          isConfigured: isConfigured,
          inputNodes: getInputNodeNames(),
          position: getNodePosition()
        }}
      />
    </div>
  );
};

export default OperationalCostsNode;
